import React, { useEffect, useState } from 'react';
import './HighScoresScreen.css';

export interface HighScore {
  companyName: string;
  gameName: string;
  score: number;
  totalSprints: number;
  totalStoriesReleased: number;
  totalEpicsCompleted: number;
  signedUpCustomers: number;
  bankBalance: number;
  employeesFired: number;
  mostStoryPointsInSprint: number;
  fewestStoryPointsInSprint: number;
  releaseVersion: number;
  date: string;
}

type SortField = 'rank' | 'gameName' | 'companyName' | 'score' | 'sprints' | 'stories' | 'epics' | 
  'customers' | 'balance' | 'fired' | 'mostPoints' | 'fewestPoints' | 'version' | 'date';
type SortDirection = 'asc' | 'desc';

interface HighScoresScreenProps {
  onBack: () => void;
}

export default function HighScoresScreen(props: HighScoresScreenProps) {
  const [highScores, setHighScores] = useState<HighScore[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('rank');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [gameNameFilter, setGameNameFilter] = useState<string>('');
  const [sprintFilter, setSprintFilter] = useState<string>('');

  useEffect(() => {
    fetchHighScores();
  }, []);

  const fetchHighScores = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/highscores');
      const data = await response.json();
      
      if (response.ok) {
        setHighScores(data.highScores || []);
      } else {
        setError(data.error || 'Failed to fetch high scores');
      }
    } catch (err) {
      setError('Failed to connect to the server');
      console.error('Error fetching high scores:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const getFilteredAndSortedScores = () => {
    let filtered = highScores;
    
    // Apply game name filter
    if (gameNameFilter.trim()) {
      filtered = filtered.filter(score => 
        score.gameName.toLowerCase().includes(gameNameFilter.toLowerCase())
      );
    }

    // Apply sprint filter
    if (sprintFilter) {
      const sprintValue = parseInt(sprintFilter, 10);
      if (!isNaN(sprintValue)) {
        filtered = filtered.filter(score => score.totalSprints === sprintValue);
      }
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      // When filtering, use filtered index for rank sorting
      const aIndex = filtered.indexOf(a);
      const bIndex = filtered.indexOf(b);

      switch (sortField) {
        case 'rank':
          comparison = aIndex - bIndex;
          break;
        case 'gameName':
          comparison = a.gameName.localeCompare(b.gameName);
          break;
        case 'companyName':
          comparison = a.companyName.localeCompare(b.companyName);
          break;
        case 'score':
          comparison = a.score - b.score;
          break;
        case 'sprints':
          comparison = a.totalSprints - b.totalSprints;
          break;
        case 'stories':
          comparison = a.totalStoriesReleased - b.totalStoriesReleased;
          break;
        case 'epics':
          comparison = a.totalEpicsCompleted - b.totalEpicsCompleted;
          break;
        case 'customers':
          comparison = a.signedUpCustomers - b.signedUpCustomers;
          break;
        case 'balance':
          comparison = a.bankBalance - b.bankBalance;
          break;
        case 'fired':
          comparison = a.employeesFired - b.employeesFired;
          break;
        case 'mostPoints':
          comparison = a.mostStoryPointsInSprint - b.mostStoryPointsInSprint;
          break;
        case 'fewestPoints':
          comparison = a.fewestStoryPointsInSprint - b.fewestStoryPointsInSprint;
          break;
        case 'version':
          comparison = a.releaseVersion - b.releaseVersion;
          break;
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  };

  const displayedScores = getFilteredAndSortedScores();
  
  // Check if we're filtering by any criteria
  const isFiltering = gameNameFilter.trim().length > 0 || sprintFilter.length > 0;

  const formatVersion = (version: number) => {
    return version.toFixed(2);
  };

  return (
    <div className="highscores-screen">
      <div className="highscores-content">
        <h1 className="highscores-title">🏆 High Scores 🏆</h1>
        
        <button className="back-button" onClick={props.onBack}>
          ← Back to Menu
        </button>

        {loading && (
          <div className="loading-message">
            <p>Loading high scores...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <p>⚠️ {error}</p>
            <button onClick={fetchHighScores}>Try Again</button>
          </div>
        )}

        {!loading && !error && highScores.length === 0 && (
          <div className="empty-message">
            <p>No high scores yet. Be the first to play!</p>
          </div>
        )}

        {!loading && !error && highScores.length > 0 && (
          <>
            <div className="filter-container">
              <input
                type="text"
                className="game-name-filter"
                placeholder="Filter by game name..."
                value={gameNameFilter}
                onChange={(e) => setGameNameFilter(e.target.value)}
              />
              <select
                className="sprint-filter"
                value={sprintFilter}
                onChange={(e) => setSprintFilter(e.target.value)}
                aria-label="Filter by sprints"
              >
                <option value="">All Sprints</option>
                <option value="5">5 Sprints</option>
                <option value="6">6 Sprints</option>
                <option value="7">7 Sprints</option>
                <option value="8">8 Sprints</option>
                <option value="9">9 Sprints</option>
                <option value="10">10 Sprints</option>
              </select>
            </div>
            <div className="highscores-list">
              <table className="highscores-table">
                <thead>
                  <tr>
                    <th className="rank-col sortable" onClick={() => handleSort('rank')}>
                      Rank{getSortIndicator('rank')}
                    </th>
                    <th className="game-name-col sortable" onClick={() => handleSort('gameName')}>
                      Game Name{getSortIndicator('gameName')}
                    </th>
                    <th className="company-col sortable" onClick={() => handleSort('companyName')}>
                      Company{getSortIndicator('companyName')}
                    </th>
                    <th className="score-col sortable" onClick={() => handleSort('score')}>
                      Score{getSortIndicator('score')}
                    </th>
                    <th className="sprints-col sortable" onClick={() => handleSort('sprints')}>
                      Sprints{getSortIndicator('sprints')}
                    </th>
                    <th className="stories-col sortable" onClick={() => handleSort('stories')}>
                      Stories{getSortIndicator('stories')}
                    </th>
                    <th className="epics-col sortable" onClick={() => handleSort('epics')}>
                      Epics{getSortIndicator('epics')}
                    </th>
                    <th className="customers-col sortable" onClick={() => handleSort('customers')}>
                      Customers{getSortIndicator('customers')}
                    </th>
                    <th className="balance-col sortable" onClick={() => handleSort('balance')}>
                      Balance{getSortIndicator('balance')}
                    </th>
                    <th className="fired-col sortable" onClick={() => handleSort('fired')}>
                      Fired{getSortIndicator('fired')}
                    </th>
                    <th className="most-points-col sortable" onClick={() => handleSort('mostPoints')}>
                      Most SP{getSortIndicator('mostPoints')}
                    </th>
                    <th className="fewest-points-col sortable" onClick={() => handleSort('fewestPoints')}>
                      Fewest SP{getSortIndicator('fewestPoints')}
                    </th>
                    <th className="version-col sortable" onClick={() => handleSort('version')}>
                      Version{getSortIndicator('version')}
                    </th>
                    <th className="date-col sortable" onClick={() => handleSort('date')}>
                      Date{getSortIndicator('date')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedScores.map((score, index) => {
                    // When filtering, use the filtered index for ranking
                    // Otherwise, find the original position in the highScores array
                    const rankIndex = isFiltering ? index : highScores.indexOf(score);
                    return (
                      <tr key={index} className={rankIndex < 3 ? `top-${rankIndex + 1}` : ''}>
                        <td className="rank-col">
                          {rankIndex === 0 && '🥇'}
                          {rankIndex === 1 && '🥈'}
                          {rankIndex === 2 && '🥉'}
                          {rankIndex > 2 && `${rankIndex + 1}`}
                        </td>
                        <td className="game-name-col">{score.gameName || '-'}</td>
                        <td className="company-col">{score.companyName}</td>
                        <td className="score-col">{score.score.toLocaleString()}</td>
                        <td className="sprints-col">{score.totalSprints}</td>
                        <td className="stories-col">{score.totalStoriesReleased}</td>
                        <td className="epics-col">{score.totalEpicsCompleted}</td>
                        <td className="customers-col">{score.signedUpCustomers}</td>
                        <td className={`balance-col ${score.bankBalance < 0 ? 'negative' : 'positive'}`}>
                          £{score.bankBalance}
                        </td>
                        <td className="fired-col">{score.employeesFired}</td>
                        <td className="most-points-col">{score.mostStoryPointsInSprint}</td>
                        <td className="fewest-points-col">{score.fewestStoryPointsInSprint}</td>
                        <td className="version-col">{formatVersion(score.releaseVersion)}</td>
                        <td className="date-col">{formatDate(score.date)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
